import { Component, OnInit, Inject, ViewChild, Output, EventEmitter } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from "@angular/material/dialog";
import { MatTable } from "@angular/material/table";
import { PickerItem } from '../../models/picker-item.model';
import { Observable } from 'rxjs';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { debounceTime, switchMap } from 'rxjs/operators';
import { MaxPickerService } from '../../services/max-picker.service';
import { RoleService } from '../../services/role.service';
import { RoleGroupMember } from '../../models/role-group-member.model';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '../../dialogs/confirmation/confirmation-dialog.component';
import { FolderService } from '../../services/folder.service';
import { AccessibilityHelper } from '../../accessibility.helper';
import { Role } from '../../models/role.model';

@Component({
    selector: 'app-roles',
    templateUrl: './role-membership.dialog.html',
    styleUrls: ['./role-membership.dialog.scss', '../../components/properties.scss'],
    standalone: false
})
export class RoleMembershipDialog implements OnInit {
    public role: Role;
    public roleName: string;
    public entityName: string;
    public roleId: number;
    public members: RoleGroupMember[];
    public allMembers: RoleGroupMember[];
    public membersAreInherited: boolean;
    public originalMembers: any[]; // doesn't need to actually contain full RoleMembers
    public updatedlMembers: any[]; // as above
    public folderId: number;
    public pickerItems: Observable<PickerItem[]>; // observable of PickerItems which will be observed in the template via use of the async pipe
    public displayedColumns: string[]; // populate on init, as this the columns are dynamic
    public showLoadingIndicator: boolean;
    public dirty: boolean;
    public folderOrSitelevel: string;

    private pickerLoadingTmeoutId: any;
    private originalMembersAreInherited: boolean;
    private membersCreatedOrDeleted: boolean = false;

    @Output()
    memberRemoved: EventEmitter<RoleGroupMember> = new EventEmitter();

    @ViewChild('roleMemberTable')
    roleMemberTable: MatTable<RoleGroupMember>;

    addMemberForm: UntypedFormGroup;
    formControls: any = {
        id: -1,
        user: [{}]
    };

    constructor(
        private fb: UntypedFormBuilder,
        private maxPickerService: MaxPickerService,
        private roleService: RoleService,
        private folderService: FolderService,
        @Inject(MAT_DIALOG_DATA) data,
        public dialog: MatDialog,
        private dialogRef: MatDialogRef<RoleMembershipDialog>) {

        this.role = data.role;
        this.roleName = data.roleName;
        this.entityName = data.entityName;
        this.roleId = data.roleId;
        this.allMembers = data.members;
        this.members = this.filterInheritedMembers(data.members, data.membersAreInherited);
        this.membersAreInherited = data.membersAreInherited;
        this.originalMembersAreInherited = this.membersAreInherited; // used for comparison on save
        this.originalMembers = this.members.map(x => { return { selected: x.selected, id: x.id } }); // make clone for comparisons after updating (don't need all properties)
        this.folderId = data.folderId;
        this.folderOrSitelevel = data.folderOrSitelevel;
    }

    ngOnInit() {
        // using reactive form for one field may seem overkill here, rather than mgModel,
        // but it makes autocomplete simple to set up
        this.addMemberForm = this.fb.group(this.formControls);
        this.initializeFilteredUsers();
        this.initializeLoadingIndicator();
        this.displayedColumns = ['name', 'actions'];    // pharv 9/20/21 uncomment this to renable different behaviors at Site vs Folder level:
        // this.isFolderLevel() ? ['select', 'name'] : ['name', 'actions'];
    }

    // Returns true if the roles are being managed at the site level rather than at a folder level
    public isSiteLevel(): boolean {
        return this.folderOrSitelevel == 'site';
    }

    // Returns true if the roles are being managed at the folder level rather than at the site level
    public isFolderLevel(): boolean {
        return this.folderOrSitelevel == 'folder';
    }

    deleteMember(event: any, member: RoleGroupMember): void {
        let func = () => {
            this.roleService.deleteMember(member.id, this.folderId).then(() => {
                this.members.splice(this.members.findIndex(x => x.id == member.id), 1);
                this.roleMemberTable.renderRows();
                this.membersCreatedOrDeleted = true;
            });
        }

        //if (this.isSiteLevel()) {
        //    this.deleteOnConfirm(1, func);
        //} else {
            func();
        //}
    }

    deleteAllMembers(): void {
        let iDs = this.members.map(x => { return x.id });
        
        this.deleteOnConfirm(this.members.length, () => {
            this.roleService.deleteAllRoleMembers(this.roleId, this.folderId).then(() => { });
            // no need to wait for the call to return
            this.members = [];
            this.roleMemberTable.renderRows();
            this.membersCreatedOrDeleted = true;
        });
    }

    addMember(): void {
        let rm = new RoleGroupMember(this.addMemberForm.value.user.key, this.addMemberForm.value.user.isUser);
        this.addMemberForm.reset(this.formControls);

        if (rm.member == undefined) {
            console.log('added member is not defined');
            return;
        }

        let memberFound = this.allMembers.filter(m => { return m.memberId == this.addMemberForm.value.user.key })[0];
        if (memberFound != null) {
            memberFound.selected = true;
            this.members.push(memberFound);
            this.roleMemberTable.renderRows();
            this.dirty = true;
            //this.addMemberForm.reset(this.formControls);
            this.membersCreatedOrDeleted = true;
            AccessibilityHelper.setFocus('#add-role-member-input');
            return;
        }

        //prevent adding duplicate members
        let newUser = true;
        this.members.forEach(function (oldMember) {
            if (rm.member == oldMember.memberId) {
                newUser = false;
                return;
            }
        });

        if (newUser) {
            this.roleService.addMember(this.roleId, this.folderId, rm).then(member => {
                //member.selected = true; // needed to avoid a weird bug
                this.members.push(member);
                this.roleMemberTable.renderRows();
                this.dirty = true;
                this.membersCreatedOrDeleted = true;
                AccessibilityHelper.setFocus('#add-role-member-input');
            });
        }
    }

    // This method is for Folder-level Roles configuration.
    // (It does not get called and is irrelevant to Site - level Roles.)
    saveSelectedMembers(): void {
        //if (this.membersAreInherited && this.originalMembersAreInherited) {return;}
        let updatedMembers = this.members.map(x => { return { selected: x.selected, id: x.id } });
        let changesMade = JSON.stringify(updatedMembers) != JSON.stringify(this.originalMembers);

        //  1) Originally members were inherited, now they're not - Create RoleGroup
        if (this.originalMembersAreInherited && !this.membersAreInherited) {
            console.log(`   ==> CREATE NEW RoleGroup for Folder:${this.folderId}!`);
            this.folderService.createRoleGroup(this.folderId, updatedMembers.filter(x => x.selected)).then(x => {
                this.dialogRef.close('save');
            });
        }
        // 2)  Originally members were not inherited, now they are - Delete RoleGroup
        else if (!this.originalMembersAreInherited && this.membersAreInherited) {
            console.log(`   ==> DELETE RoleGroup for Folder:${this.folderId}!`);
            this.folderService.deleteRoleGroup(this.folderId).then(x => {
                this.dialogRef.close('save');
            });
        }
        // 3)  Changes to the selected users were made - Update RoleGroup
        else if (changesMade) {
            console.log(`   ==> UPDATE RoleGroup for Folder:${this.folderId}!`);
            this.folderService.updateRoleGroup(this.folderId, this.roleId, updatedMembers.filter(x => x.selected)).then(x => {
                this.dialogRef.close('save');
            });
        }
        else {
            this.dialogRef.close();
        }
    }

    closeDialog() {
        console.log('closeDialog');
        if (this.membersCreatedOrDeleted) {
            this.membersCreatedOrDeleted = false;
            this.dialogRef.close('save');
        } else {
            this.dialogRef.close();
        }
    }

    // Used by auto complete control
    displayUserName(pi: PickerItem): string {
        return pi.displayName;
    }

    toggleInheritance(): void {
        this.dirty = true;
        this.members = this.filterInheritedMembers(this.allMembers, this.membersAreInherited);
    }

    private initializeFilteredUsers(): void {
        this.pickerItems = this.addMemberForm.controls['user'].valueChanges.pipe(
            debounceTime(300),                                          // swallow multiple valueChanges made within 300ms period and just keep the most recent one
            switchMap(value => this.maxPickerService.search(value)) // switch to a new observable, canceling the previous
        );
    }

    private initializeLoadingIndicator(): void {
        this.maxPickerService.isSearching.subscribe((value) => {
            if (value) {
                if (!this.showLoadingIndicator) {
                    this.pickerLoadingTmeoutId = setTimeout(() => { this.showLoadingIndicator = true }, 450);
                }
            }
            else {
                clearTimeout(this.pickerLoadingTmeoutId);
                this.showLoadingIndicator = false;
            }
        });
    }

    // Seems like this and other similar blocks could be factored into something reusable
    // Things that would need to be passed:
    // - this.dialog
    // - "Remove Member from Role"
    // - "Are you sure you want to remove this member? The operation cannot be undone."
    private deleteOnConfirm(numToDelete: number, callback: Function): void {
        let title = numToDelete == 1 ? `Remove Member from Role` : 'Remove Members from Role';
        let message = numToDelete == 1 ?
            `Are you sure you want to remove this member? The operation cannot be undone.` :
            `Are you sure you want to remove all members? This operation cannot be undone.`
        let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            maxWidth: "400px",
            data: new ConfirmationDialogModel(title, message)
        });
        dialogRef.afterClosed().subscribe(yes => {
            if (yes) callback();
            AccessibilityHelper.setFocus('#add-role-member-input');
        });
    }

    private filterInheritedMembers(members: RoleGroupMember[], membersAreInherited: boolean): RoleGroupMember[] {
        let filteredMembers = members;
        if (membersAreInherited) {
            filteredMembers = members.filter(m => { return !m.isInherited });
        }

        //if (this.role.memberCount() > 0) {
        return filteredMembers.filter(m => { return m.selected });
        //} else {
        //    return filteredMembers;
        //}
    }
}
